import { createValidationResolver } from '@lotta/self-service/lottery-fe/util-validation-resolver';
import type { Schema } from 'ajv';
import { useEffect } from 'react';
import type { FieldValues, UseFormProps, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFormModule = <TFieldValues extends FieldValues = FieldValues, TContext = any>(
  props: UseFormProps<TFieldValues, TContext> & {
    schema: Schema;
    moduleName: string;
    onChange: (moduleName: string, values: TFieldValues) => void;
    onValidation: (moduleName: string, isValid: boolean) => void;
  }
): UseFormReturn<TFieldValues, TContext> => {
  const { schema, moduleName, onChange, onValidation, ...rest } = props;
  const form = useForm<TFieldValues>({
    resolver: createValidationResolver<TFieldValues>(schema),
    mode: 'onTouched',
    ...rest,
  });

  const {
    formState: { isValid, errors },
    watch,
  } = form;

  useEffect(() => {
    onValidation(moduleName, isValid && Object.keys(errors).length === 0);
  }, [moduleName, isValid, onValidation, errors]);

  useEffect(() => {
    const { unsubscribe } = watch((value) => onChange(moduleName, value as TFieldValues));
    return () => unsubscribe();
  }, [moduleName, onChange, watch]);

  useEffect(() => {
    if (rest.defaultValues) {
      onChange(moduleName, rest.defaultValues as TFieldValues);
    }
  }, [moduleName, onChange, rest.defaultValues]);

  return form;
};
